import React, { useState, useEffect } from "react";
import { Printer, Loader2, CheckCircle2, AlertTriangle, Wifi, WifiOff, ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "src/shadcn/ui/dialog";
import { RelabelCaseRecord, BrowserPrintDevice } from "src/types";

type PrintStatus = "idle" | "printing" | "success" | "error";

interface PrintLabelModalProps {
  open: boolean;
  onClose: () => void;
  casesToPrint: RelabelCaseRecord[];
  printer: BrowserPrintDevice | null;
  availablePrinters: BrowserPrintDevice[];
  isPrinterConnected: boolean;
  printerError: string | null;
  onSelectPrinter: (uid: string) => void;
  onPrint: (caseId: string) => Promise<void>;
}

const PrintLabelModal: React.FC<PrintLabelModalProps> = ({
  open,
  onClose,
  casesToPrint,
  printer,
  availablePrinters,
  isPrinterConnected,
  printerError,
  onSelectPrinter,
  onPrint
}) => {
  const [printStatus, setPrintStatus] = useState<PrintStatus>("idle");
  const [printedCount, setPrintedCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPrinterSelect, setShowPrinterSelect] = useState(false);

  useEffect(() => {
    if (open) {
      setPrintStatus("idle");
      setPrintedCount(0);
      setErrorMessage(null);
    }
  }, [open]);

  const handlePrint = async () => {
    setPrintStatus("printing");
    setPrintedCount(0);
    setErrorMessage(null);

    try {
      for (let i = 0; i < casesToPrint.length; i++) {
        await onPrint(casesToPrint[i].id);
        setPrintedCount(i + 1);
      }
      setPrintStatus("success");
    } catch (err) {
      setPrintStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Failed to send label to printer");
    }
  };

  const totalLabels = casesToPrint.length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            ZPL Print Case Label{totalLabels > 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>
            {totalLabels === 1
              ? `Send label for ${casesToPrint[0]?.id} to your Zebra printer.`
              : `Send ${totalLabels} labels to your Zebra printer.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                {isPrinterConnected ? (
                  <Wifi className="w-4 h-4 text-success-600" />
                ) : (
                  <WifiOff className="w-4 h-4 text-danger-500" />
                )}
                <div>
                  <p className="text-sm font-semibold text-secondary-900">
                    {isPrinterConnected ? printer?.name || "Zebra Printer" : "No Printer Connected"}
                  </p>
                  {isPrinterConnected && printer && (
                    <p className="text-xs text-secondary-500 mt-0.5">
                      {printer.connection.toUpperCase()} &middot; {printer.uid}
                    </p>
                  )}
                  {!isPrinterConnected && (
                    <p className="text-xs text-secondary-500 mt-0.5">
                      Install Zebra Browser Print and connect a printer
                    </p>
                  )}
                </div>
              </div>
              {availablePrinters.length > 1 && (
                <button
                  type="button"
                  onClick={() => setShowPrinterSelect(!showPrinterSelect)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                >
                  Change
                  <ChevronDown className={`w-3 h-3 transition-transform ${showPrinterSelect ? "rotate-180" : ""}`} />
                </button>
              )}
            </div>

            {showPrinterSelect && availablePrinters.length > 1 && (
              <div className="mt-3 space-y-1">
                {availablePrinters.map((p) => (
                  <button
                    key={p.uid}
                    type="button"
                    onClick={() => {
                      onSelectPrinter(p.uid);
                      setShowPrinterSelect(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      printer?.uid === p.uid
                        ? "bg-primary-100 text-primary-800 font-medium"
                        : "hover:bg-secondary-100 text-secondary-700"
                    }`}
                  >
                    <span className="font-medium">{p.name}</span>
                    <span className="text-xs text-secondary-400 ml-2">{p.connection}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-secondary-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 mb-2">
              Label{totalLabels > 1 ? "s" : ""} to Print
            </p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {casesToPrint.map((c, idx) => {
                const isDone = printStatus !== "idle" && idx < printedCount;
                const isCurrent = printStatus === "printing" && idx === printedCount;
                return (
                  <div key={c.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {isDone && <CheckCircle2 className="w-3.5 h-3.5 text-success-600" />}
                      {isCurrent && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-600" />}
                      {!isDone && !isCurrent && (
                        <div className="w-3.5 h-3.5 rounded-full border border-secondary-300" />
                      )}
                      <span className="font-mono text-secondary-800">{c.id}</span>
                    </div>
                    <span className="text-xs text-secondary-500">{c.totalUnits} units</span>
                  </div>
                );
              })}
            </div>
          </div>

          {(printerError || errorMessage) && (
            <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3">
              <AlertTriangle className="w-4 h-4 text-danger-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger-700">{errorMessage || printerError}</p>
            </div>
          )}

          {printStatus === "success" && (
            <div className="flex items-start gap-2 rounded-lg border border-success-200 bg-success-50 p-3">
              <CheckCircle2 className="w-4 h-4 text-success-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-success-700">
                {totalLabels === 1
                  ? "Label sent to printer successfully."
                  : `All ${totalLabels} labels sent to printer successfully.`}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
          >
            {printStatus === "success" ? "Done" : "Cancel"}
          </button>
          {printStatus !== "success" && (
            <button
              type="button"
              onClick={handlePrint}
              disabled={!isPrinterConnected || printStatus === "printing"}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-black rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {printStatus === "printing" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Printing {printedCount}/{totalLabels}...
                </>
              ) : printStatus === "error" ? (
                <>
                  <Printer className="w-4 h-4" />
                  Retry
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4" />
                  ZPL Print {totalLabels > 1 ? `All ${totalLabels} Labels` : "Label"}
                </>
              )}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { PrintLabelModal };
